import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/Auth";
import { Field, Page } from "@/components/Layout";

const DEST = { admin: "/admin", operatore: "/aziende/pannello", acquirente: "/account" };

export default function Login({ area = "pubblico" }) {
  const az = area === "aziende";
  const { refresh } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ email: "", password: "" });
  const [challenge, setChallenge] = useState(null);
  const [codice, setCodice] = useState("");
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sp] = useSearchParams();
  const annullato = sp.get("pagamento") === "annullato";
  const scaduta = sp.get("scaduta") === "1";
  const done = async (u) => {
    await refresh();
    nav(DEST[u.ruolo] || "/");
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      if (!challenge) {
        const { data } = await api.post("/auth/login", { ...f, area });
        if (data.richiede_2fa) setChallenge(data.challenge_id);
        else await done(data.utente);
      } else {
        const { data } = await api.post("/auth/2fa", { challenge_id: challenge, codice });
        await done(data.utente);
      }
    } catch (e2) {
      setErr(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Page className="max-w-md">
      <h1 className="font-display text-4xl sm:text-5xl">{challenge ? "Verifica in due passaggi" : az ? "Accesso aziende" : "Bentornato"}</h1>
      <form onSubmit={submit} className="card mt-8 space-y-5" data-testid="login-form">
        {scaduta && !challenge && (
          <p className="text-sm p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-900" data-testid="sessione-scaduta-msg">Sessione scaduta dopo 30 minuti di inattività: accedi di nuovo.</p>
        )}
        {annullato && !challenge && (
          <p className="text-sm p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-900" data-testid="pagamento-annullato-msg">
            Pagamento del piano non completato. La registrazione è salvata: conferma l'email, accedi e paga il piano dalla sezione "Piano spazio".
          </p>
        )}
        {!challenge ? (
          <>
            <Field label="Email"><input type="email" required className="field" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="login-email-input" /></Field>
            <Field label="Password"><input type="password" required className="field" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="login-password-input" /></Field>
          </>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-[#786E65] flex gap-2"><ShieldCheck size={18} className="text-[#7E5BAF] shrink-0" />Ti abbiamo inviato un codice di 6 cifre via email. Scade tra 10 minuti.</p>
            <input inputMode="numeric" maxLength={6} className="field font-code text-2xl tracking-[0.5em] text-center" value={codice}
              onChange={(e) => setCodice(e.target.value.replace(/\D/g, ""))} data-testid="otp-input" autoFocus />
          </div>
        )}
        {err && <p className="text-sm text-red-700" data-testid="login-error">{err}</p>}
        <button className="btn-primary w-full" disabled={busy || (challenge && codice.length !== 6)} data-testid="login-submit-btn">
          {busy ? "Attendere…" : challenge ? "Conferma codice" : "Accedi"}
        </button>
        {!challenge && (
          <div className="flex justify-between text-sm">
            <Link to={az ? "/aziende/password-dimenticata" : "/password-dimenticata"} className="text-[#7E5BAF] hover:underline" data-testid="forgot-link">Password dimenticata?</Link>
            {!az && <Link to="/registrati" className="text-[#7E5BAF] hover:underline" data-testid="register-link">Crea un account</Link>}
          </div>
        )}
      </form>
    </Page>
  );
}
